import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardBody } from "react-bootstrap";
import StatsOverview from "../../Posts/StatsOverview";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import DonutChart from "../../Charts/DonutChart";
import ApexChart from "../../Charts/ApexChart";

const GroupDashboard = ({
  dashboardData,
  totalStatistics,
  mostLikedPost,
  group,
  shares = [],
}) => {
  const { t } = useTranslation();

  const statusData = useMemo(() => {
    const counts = {};
    (shares ?? []).forEach((s) => {
      const post = s?.post;
      if (!post) return;
      const status = post.status ?? (post.draft ? "draft" : "unknown");
      counts[status] = (counts[status] ?? 0) + 1;
    });
    return {
      labels: Object.keys(counts),
      series: Object.values(counts),
    };
  }, [shares]);

  const memberData = useMemo(() => {
    const counts = {};
    (shares ?? []).forEach((s) => {
      if (!s?.post) return;
      const name = s?.userinfo?.username ?? "Unknown";
      counts[name] = (counts[name] ?? 0) + 1;
    });
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return {
      chartCategories: entries.map(([k]) => k),
      chartSeries: [{ name: "Posts", data: entries.map(([, v]) => v) }],
    };
  }, [shares]);

  const pillarData = useMemo(() => {
    const counts = {};
    (shares ?? []).forEach((s) => {
      if (!s?.post) return;
      const pillar = s.post.content_pillar?.pillar ?? "No Pillar";
      counts[pillar] = (counts[pillar] ?? 0) + 1;
    });
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return {
      chartCategories: entries.map(([k]) => k),
      chartSeries: [{ name: "Posts", data: entries.map(([, v]) => v) }],
    };
  }, [shares]);

  const postTypeData = useMemo(() => {
    const counts = {};
    (shares ?? []).forEach((s) => {
      if (!s?.post) return;
      const type = s.post.post_type ?? "unknown";
      counts[type] = (counts[type] ?? 0) + 1;
    });
    return {
      labels: Object.keys(counts),
      series: Object.values(counts),
    };
  }, [shares]);

  return (
    <>
      {group.delete_date && (
        <Row
          className="mb-5 mt-5 pt-5 pb-5"
          style={{ backgroundColor: "red", color: "white", fontWeight: "Bold" }}
        >
          <Col>
            Group will be deleted: {formatDate(group.delete_date, true)}
          </Col>
        </Row>
      )}

      <Row>
        <Col md={6}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-primary-subtle text-primary rounded-circle font-size-18">
                      <i className="uil uil-list-ul"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <p className="mb-1 text-truncate text-muted">
                    {t("Backend.Dashboard.TotalPosts")}
                  </p>
                  <h5 className="font-size-16 mb-0">{dashboardData?.length}</h5>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-primary-subtle text-primary rounded-circle font-size-18">
                      <i className="uil uil-list-ul"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <p className="mb-1 text-truncate text-muted">
                    {t("Backend.Groups.Dashboard.TotalPerformance")}
                  </p>
                  <h5 className="font-size-16 mb-0">
                    <StatsOverview post={totalStatistics} />
                  </h5>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-primary-subtle text-primary rounded-circle font-size-18">
                      <i className="uil uil-list-ul"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <p className="mb-1 text-truncate text-muted">
                    {t("Backend.Groups.Dashboard.MostLikedPost")}
                  </p>
                  <h5 className="font-size-16 mb-0">{mostLikedPost?.title}</h5>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={6}>
          <DonutChart
            chartLabels={statusData.labels}
            series={statusData.series}
            title="Post Status"
            type="donut"
          />
        </Col>
        <Col md={6}>
          <DonutChart
            chartLabels={postTypeData.labels}
            series={postTypeData.series}
            title="Post Type Mix"
            type="pie"
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={6}>
          <ApexChart
            key={memberData.chartCategories.join(",")}
            title="Posts per Member"
            data={memberData}
          />
        </Col>
        <Col md={6}>
          <ApexChart
            key={pillarData.chartCategories.join(",")}
            title="Posts per Content Pillar"
            data={pillarData}
          />
        </Col>
      </Row>
    </>
  );
};

export default GroupDashboard;
